import { allPermissions, permissionsIn } from './permission-groups';
import { Permissions } from './permissions';

/**
 * A set of permissions that goes together, for giving a user the usual set for a job in one click.
 */
export interface PermissionRole {
    key: 'admin' | 'accountant' | 'servicecenter' | 'csr' | 'sales';
    name: string;
    description: string;
    icon: string;
    permissions: Permissions[];
}

export const PermissionRoles: PermissionRole[] = [
    {
        key: 'accountant',
        name: 'Accountant',
        description: 'Allows a user to view and download invoices, see shipments, and enter credits.',
        icon: 'la-money',
        permissions: [
            Permissions.Dashboard,
            Permissions.OrderingOrders,
            Permissions.ShippingPickupsView, Permissions.ShippingPickupsEdit, Permissions.ShippingPickupsDelete,
            Permissions.ShippingTrucksView, Permissions.ShippingTrucksEdit, Permissions.ShippingTrucksDelete,
            Permissions.InvoicingInvoices, Permissions.InvoicingCredits,
            Permissions.InvoicingBillingOnlyView, Permissions.InvoicingBillingOnlyInvoice,
            Permissions.SupportDealersView,
            ...permissionsIn('Reports > Accounting'),
            ...permissionsIn('Communications')
        ]
    },
    {
        key: 'servicecenter',
        name: 'Service Center',
        description: 'Allows a user to view and create shipments at a service center.',
        icon: 'la-truck',
        permissions: [
            Permissions.Dashboard,
            Permissions.OrderingOrders,
            Permissions.ShippingPickupsView, Permissions.ShippingPickupsEdit, Permissions.ShippingPickupsDelete,
            Permissions.ShippingTrucksView, Permissions.ShippingTrucksEdit, Permissions.ShippingTrucksDelete,
            Permissions.InvoicingInvoices,
            Permissions.SupportDealersView
        ]
    },
    {
        key: 'csr',
        name: 'Customer Service',
        description: 'Allows a user to view shipments, search invoices, and view communication activity feed.',
        icon: 'la-user',
        permissions: [
            Permissions.Dashboard,
            Permissions.OrderingOrders,
            Permissions.InvoicingInvoices,
            Permissions.CommunicationsActivityFeedView, Permissions.CommunicationsActivityFeedResend,
            Permissions.SupportDealersView,
            Permissions.InventoryPhysicalControlGroupsView, Permissions.InventoryPhysicalControlGroupsEdit,
            Permissions.ShippingTrucksView, Permissions.ShippingContainersView, Permissions.ShippingPickupsView
        ]
    },
    {
        key: 'sales',
        name: 'Sales',
        description: 'Allows a user to view shipments, search invoices, view communication activity feed, and see dealer sales.',
        icon: 'la-briefcase',
        permissions: [
            Permissions.Dashboard,
            Permissions.OrderingOrders,
            Permissions.InvoicingInvoices,
            Permissions.CommunicationsActivityFeedView, Permissions.CommunicationsActivityFeedResend,
            Permissions.SupportDealersView, Permissions.SupportDealersSales,
            Permissions.ShippingTrucksView, Permissions.ShippingContainersView, Permissions.ShippingPickupsView
        ]
    },
    {
        key: 'admin',
        name: 'Admin',
        description: 'Gives the user access to all features and functionality within the system.',
        icon: 'la-cog',
        permissions: allPermissions()
    }
];

/** Which role, if any, is exactly what a user holds */
export const roleOf = (held: Permissions[] | undefined): PermissionRole['key'] | 'none' | 'custom' => {
    const have = new Set(held || []);
    if (have.size === 0) {
        return 'none';
    }

    const match = PermissionRoles.find(role => role.permissions.length === have.size && role.permissions.every(x => have.has(x)));
    return match ? match.key : 'custom';
};
